import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Bot, Building2, CheckCircle2, ChevronRight, Copy, Plus, RotateCcw, Search, Settings2, Sparkles, Workflow, Send, MessageCircle, Zap, Code2 } from 'lucide-react'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, Input, Label, PageHeader, Textarea } from '@/components/ui'
import { cn } from '@/lib/cn'

export const Route = createFileRoute('/store/$id/agents')({
  component: AgentsPage,
})

type AgentStatus = 'active' | 'draft' | 'paused'
type EditorTab = 'visual' | 'prompt' | 'preview'
type ChatTab = 'test' | 'flow'

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

interface AgentRecord {
  id: string
  tenantName: string
  tenantSlug: string
  agentName: string
  objective: string
  status: AgentStatus
  model: string
  temperature: number
  prompt: string
  questions: string[]
  products: string[]
  paymentProvider: string
  updatedAt: string
}

const SEED_AGENTS: AgentRecord[] = [
  {
    id: 'ag-001',
    tenantName: 'Centrio Beauty',
    tenantSlug: 'centrio-beauty',
    agentName: 'Sales Concierge',
    objective: 'Ayuda a descubrir productos y cerrar ventas con recomendaciones de rutina.',
    status: 'active',
    model: 'gpt-4o-mini',
    temperature: 0.4,
    prompt: 'Eres un asistente experto en belleza. Detecta intención, recomienda {{productos}} y guía al usuario con empatía. Usuario: {{nombreCliente}}',
    questions: ['¿Qué tipo de piel tienes?', '¿Cuál es tu objetivo principal?', '¿Qué presupuesto manejas?'],
    products: ['Sérum vitamina C', 'Crema hidratante oil-free', 'Protector solar SPF 50'],
    paymentProvider: 'Stripe',
    updatedAt: 'Hace 12 min',
  },
  {
    id: 'ag-002',
    tenantName: 'Urban Fit',
    tenantSlug: 'urban-fit',
    agentName: 'Trainer Builder',
    objective: 'Arma planes de entrenamiento y sugiere suplementos según el perfil.',
    status: 'draft',
    model: 'gpt-4.1',
    temperature: 0.2,
    prompt: 'Eres un coach técnico para {{nombreCliente}}. Mantén respuestas cortas, precisas y orientadas a conversión.',
    questions: ['¿Objetivo físico?', '¿Nivel actual?', '¿Entrenas en casa o gimnasio?'],
    products: ['Creatina monohidratada', 'Proteína whey', 'Bandas elásticas'],
    paymentProvider: 'Mercado Pago',
    updatedAt: 'Hoy',
  },
  {
    id: 'ag-003',
    tenantName: 'North Home',
    tenantSlug: 'north-home',
    agentName: 'Home Advisor',
    objective: 'Recomienda productos para el hogar y apoya postventa.',
    status: 'paused',
    model: 'gpt-4o-mini',
    temperature: 0.5,
    prompt: 'Eres un asesor de catálogo especializado en {{categoría}}. Prioriza claridad, compara {{productos}} y evita respuestas genéricas.',
    questions: ['¿Para qué espacio es?', '¿Qué estilo prefieres?', '¿Necesitas envío urgente?'],
    products: ['Lámpara de mesa', 'Silla ergonómica', 'Organizador modular'],
    paymentProvider: 'Stripe',
    updatedAt: 'Ayer',
  },
]

const AVAILABLE_VARIABLES = [
  { key: 'nombreCliente', label: 'Nombre del cliente', desc: 'Se completa en runtime' },
  { key: 'categoría', label: 'Categoría de producto', desc: 'Detectada automáticamente' },
  { key: 'productos', label: 'Lista de productos', desc: 'Array de items disponibles' },
  { key: 'presupuesto', label: 'Presupuesto estimado', desc: 'Basado en historial' },
  { key: 'idioma', label: 'Idioma del usuario', desc: 'es, en, pt' },
  { key: 'dispositivo', label: 'Tipo de dispositivo', desc: 'mobile, tablet, desktop' },
]

function AgentsPage() {
  const [agents, setAgents] = useState(SEED_AGENTS)
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | AgentStatus>('all')
  const [selectedId, setSelectedId] = useState(SEED_AGENTS[0]?.id ?? '')
  const [editorTab, setEditorTab] = useState<EditorTab>('visual')
  const [chatTab, setChatTab] = useState<ChatTab>('test')
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [chatInput, setChatInput] = useState('')
  const [copiedPrompt, setCopiedPrompt] = useState(false)

  const selectedAgent = agents.find((agent) => agent.id === selectedId) ?? agents[0] ?? null

  const filteredAgents = useMemo(
    () => agents.filter((agent) => {
      const matchesQuery = [agent.tenantName, agent.agentName, agent.objective, agent.paymentProvider]
        .join(' ')
        .toLowerCase()
        .includes(query.trim().toLowerCase())
      const matchesStatus = statusFilter === 'all' ? true : agent.status === statusFilter
      return matchesQuery && matchesStatus
    }),
    [agents, query, statusFilter],
  )

  const metrics = useMemo(() => ({
    total: agents.length,
    active: agents.filter((agent) => agent.status === 'active').length,
    drafts: agents.filter((agent) => agent.status === 'draft').length,
    tenants: new Set(agents.map((agent) => agent.tenantSlug)).size,
  }), [agents])

  function updateSelected(patch: Partial<AgentRecord>) {
    if (!selectedAgent) return
    setAgents((current) => current.map((agent) => agent.id === selectedAgent.id ? { ...agent, ...patch, updatedAt: 'Ahora' } : agent))
  }

  const promptVariables = useMemo(() => {
    if (!selectedAgent) return []
    const regex = /{{(\w+)}}/g
    const matches = [...selectedAgent.prompt.matchAll(regex)]
    return [...new Set(matches.map((m) => m[1]))]
  }, [selectedAgent])

  function copyPrompt() {
    if (!selectedAgent) return
    void navigator.clipboard.writeText(selectedAgent.prompt)
    setCopiedPrompt(true)
    window.setTimeout(() => setCopiedPrompt(false), 1500)
  }

  function insertVariable(varKey: string) {
    const textarea = document.querySelector('textarea[data-prompt]') as HTMLTextAreaElement | null
    if (!textarea || !selectedAgent) return
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const newValue = selectedAgent.prompt.slice(0, start) + `{{${varKey}}}` + selectedAgent.prompt.slice(end)
    updateSelected({ prompt: newValue })
  }

  function sendChatMessage() {
    if (!chatInput.trim() || !selectedAgent) return
    const userMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: chatInput,
      timestamp: new Date(),
    }
    setChatMessages((prev) => [...prev, userMsg])
    setChatInput('')

    // Simular respuesta del agente
    setTimeout(() => {
      const assistantMsg: ChatMessage = {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content: `Soy ${selectedAgent.agentName}. ${selectedAgent.objective} - Basándome en tu pregunta: "${userMsg.content}" te puedo ayudar con...`,
        timestamp: new Date(),
      }
      setChatMessages((prev) => [...prev, assistantMsg])
    }, 500)
  }

  return (
    <>
      <PageHeader
        title="Agentes IA"
        description="Panel global para parametrizar y supervisar todos los agentes por empresa"
        action={<Button><Plus className="size-4 mr-2" />Nuevo agente</Button>}
      />

      <div className="p-8 space-y-6">
        <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Metric icon={Bot} label="Agentes totales" value={metrics.total} hint="global" color="text-cyan-700 dark:text-cyan-400" />
          <Metric icon={CheckCircle2} label="Activos" value={metrics.active} hint="publicados" color="text-green-700 dark:text-green-400" />
          <Metric icon={Settings2} label="Borradores" value={metrics.drafts} hint="por revisar" color="text-yellow-700 dark:text-yellow-400" />
          <Metric icon={Building2} label="Empresas" value={metrics.tenants} hint="tenants con IA" color="text-purple-700 dark:text-purple-400" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-[1.1fr_1.4fr] gap-6 items-start">
          <Card>
            <CardHeader className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <CardTitle>Listado global</CardTitle>
                <Badge variant="muted">{filteredAgents.length} resultados</Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar agente, empresa o pasarela" className="pl-9" />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'all' | AgentStatus)}
                  className="h-9 rounded-md border border-border bg-[hsl(var(--surface))] px-3 text-sm text-foreground"
                >
                  <option value="all">Todos</option>
                  <option value="active">Activos</option>
                  <option value="draft">Borradores</option>
                  <option value="paused">Pausados</option>
                </select>
              </div>
            </CardHeader>

            <CardContent className="space-y-3">
              {filteredAgents.length === 0 ? (
                <EmptyState
                  title="No hay agentes con esos filtros"
                  description="Probá cambiar la búsqueda o mostrar todos los estados."
                />
              ) : (
                filteredAgents.map((agent) => (
                  <button
                    key={agent.id}
                    type="button"
                    onClick={() => { setSelectedId(agent.id); setChatMessages([]) }}
                    className={cn(
                      'w-full rounded-lg border p-4 text-left transition',
                      selectedId === agent.id
                        ? 'border-cyan-200 dark:border-cyan-700 bg-cyan-50 dark:bg-cyan-950/30'
                        : 'border-border bg-background/40 hover:border-border',
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-foreground">{agent.agentName}</p>
                          <StatusBadge status={agent.status} />
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{agent.tenantName} · {agent.paymentProvider}</p>
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{agent.objective}</p>
                      </div>
                      <ChevronRight className="size-4 text-muted-foreground mt-1" />
                    </div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>

          {selectedAgent && (
            <div className="space-y-6">
              {/* HEADER */}
              <Card>
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle>{selectedAgent.agentName}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">{selectedAgent.tenantName}</p>
                    </div>
                    <StatusBadge status={selectedAgent.status} />
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge variant="muted">Modelo: {selectedAgent.model}</Badge>
                    <Badge variant="muted">Temp: {selectedAgent.temperature.toFixed(1)}</Badge>
                  </div>
                </CardHeader>
              </Card>

              {/* TABS EDITOR */}
              <Card>
                <div className="border-b border-border flex gap-0">
                  <button
                    onClick={() => setEditorTab('visual')}
                    className={cn(
                      'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition',
                      editorTab === 'visual'
                        ? 'border-cyan-500 text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Code2 className="size-4 inline mr-2" />
                    Editor visual
                  </button>
                  <button
                    onClick={() => setEditorTab('prompt')}
                    className={cn(
                      'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition',
                      editorTab === 'prompt'
                        ? 'border-cyan-500 text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Sparkles className="size-4 inline mr-2" />
                    Prompt raw
                  </button>
                  <button
                    onClick={() => setEditorTab('preview')}
                    className={cn(
                      'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition',
                      editorTab === 'preview'
                        ? 'border-cyan-500 text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Workflow className="size-4 inline mr-2" />
                    Preview
                  </button>
                </div>

                <CardContent className="pt-4">
                  {editorTab === 'visual' && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="agent-name">Nombre del agente</Label>
                        <Input id="agent-name" value={selectedAgent.agentName} onChange={(e) => updateSelected({ agentName: e.target.value })} />
                      </div>
                      <div>
                        <Label htmlFor="objective">Objetivo</Label>
                        <Textarea id="objective" value={selectedAgent.objective} onChange={(e) => updateSelected({ objective: e.target.value })} />
                      </div>
                      <div>
                        <Label>Variables disponibles</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {AVAILABLE_VARIABLES.map((v) => (
                            <button
                              key={v.key}
                              type="button"
                              onClick={() => insertVariable(v.key)}
                              className="rounded-lg border border-border bg-background/50 p-3 text-left text-xs hover:bg-[hsl(var(--surface))] transition"
                            >
                              <p className="font-mono text-cyan-700 dark:text-cyan-400">{'{{' + v.key + '}}'}</p>
                              <p className="text-muted-foreground mt-1">{v.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="model">Modelo</Label>
                          <Input id="model" value={selectedAgent.model} onChange={(e) => updateSelected({ model: e.target.value })} />
                        </div>
                        <div>
                          <Label htmlFor="temperature">Temperatura</Label>
                          <Input
                            id="temperature"
                            type="number"
                            step="0.1"
                            min="0"
                            max="2"
                            value={selectedAgent.temperature}
                            onChange={(e) => updateSelected({ temperature: Number(e.target.value) })}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {editorTab === 'prompt' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <Label htmlFor="prompt">Prompt base</Label>
                        <Button type="button" variant="ghost" size="sm" onClick={copyPrompt}>
                          <Copy className="size-3.5 mr-2" />{copiedPrompt ? 'Copiado' : 'Copiar'}
                        </Button>
                      </div>
                      <Textarea
                        id="prompt"
                        data-prompt="true"
                        value={selectedAgent.prompt}
                        onChange={(e) => updateSelected({ prompt: e.target.value })}
                        className="min-h-[200px] font-mono text-xs"
                        placeholder="Usa {{variable}} para insertar dinámicamente"
                      />
                      {promptVariables.length > 0 && (
                        <div className="p-3 rounded-lg bg-background/60 border border-border text-xs">
                          <p className="text-muted-foreground mb-2">Variables detectadas en el prompt:</p>
                          <div className="flex flex-wrap gap-2">
                            {promptVariables.map((v) => (
                              <Badge key={v} variant="muted">{'{{' + v + '}}'}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {editorTab === 'preview' && (
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-foreground mb-3">Flujo de preguntas</p>
                        <div className="space-y-2">
                          {selectedAgent.questions.map((q, idx) => (
                            <div key={idx} className="rounded-lg border border-border bg-background/40 p-3">
                              <div className="flex items-start gap-2">
                                <span className="text-xs font-bold text-cyan-700 dark:text-cyan-400 mt-1">P{idx + 1}</span>
                                <p className="text-sm text-foreground">{q}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground mb-3">Productos relacionados</p>
                        <div className="grid grid-cols-1 gap-2">
                          {selectedAgent.products.map((p, idx) => (
                            <div key={idx} className="rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-950/20 p-3">
                              <p className="text-sm text-emerald-700 dark:text-emerald-300">✓ {p}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* TABS CHAT */}
              <Card>
                <div className="border-b border-border flex gap-0">
                  <button
                    onClick={() => setChatTab('test')}
                    className={cn(
                      'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition',
                      chatTab === 'test'
                        ? 'border-cyan-500 text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <MessageCircle className="size-4 inline mr-2" />
                    Testear agente
                  </button>
                  <button
                    onClick={() => setChatTab('flow')}
                    className={cn(
                      'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition',
                      chatTab === 'flow'
                        ? 'border-cyan-500 text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Zap className="size-4 inline mr-2" />
                    Conversión
                  </button>
                </div>

                <CardContent className="pt-4">
                  {chatTab === 'test' && (
                    <div className="space-y-4">
                      <div className="rounded-lg border border-border bg-background/40 h-64 overflow-y-auto p-4 space-y-3">
                        {chatMessages.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center mt-24">Escribe un mensaje para probar el agente</p>
                        ) : (
                          chatMessages.map((msg) => (
                            <div
                              key={msg.id}
                              className={cn(
                                'flex gap-2',
                                msg.role === 'user' ? 'justify-end' : 'justify-start',
                              )}
                            >
                              <div
                                className={cn(
                                  'max-w-xs rounded-lg p-3 text-sm',
                                  msg.role === 'user'
                                    ? 'bg-cyan-600 text-foreground'
                                    : 'bg-[hsl(var(--muted))] text-foreground',
                                )}
                              >
                                {msg.content}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') sendChatMessage() }}
                          placeholder="Escribe un mensaje..."
                          className="flex-1"
                        />
                        <Button type="button" onClick={sendChatMessage}>
                          <Send className="size-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {chatTab === 'flow' && (
                    <div className="space-y-4 text-sm">
                      <p className="text-muted-foreground">Modelo de conversión del agente</p>
                      <div className="space-y-2">
                        <div className="rounded-lg border border-border bg-background/40 p-3">
                          <p className="text-xs text-muted-foreground mb-1">Objetivo</p>
                          <p className="text-foreground">{selectedAgent.objective}</p>
                        </div>
                        <div className="rounded-lg border border-border bg-background/40 p-3">
                          <p className="text-xs text-muted-foreground mb-1">Pasos del flujo</p>
                          <ol className="list-decimal list-inside space-y-1 text-foreground">
                            <li>Usuario entra y ve las {selectedAgent.questions.length} preguntas clave</li>
                            <li>Agente parametriza respuestas y detecta necesidad</li>
                            <li>Sugiere {selectedAgent.products.length} productos relevantes</li>
                            <li>Redirecciona a checkout con contexto</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ACCIONES */}
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={() => updateSelected({ status: selectedAgent.status === 'active' ? 'paused' : 'active' })}>
                  <Sparkles className="size-4 mr-2" />
                  {selectedAgent.status === 'active' ? 'Pausar' : 'Activar'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setAgents(SEED_AGENTS)}>
                  <RotateCcw className="size-4 mr-2" />
                  Reponer demo
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>
    </>
  )
}

function Metric({ icon: Icon, label, value, hint, color }: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: number
  hint: string
  color: string
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className="text-3xl font-bold mt-2">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{hint}</p>
          </div>
          <Icon className={cn('size-7', color)} />
        </div>
      </CardContent>
    </Card>
  )
}

function StatusBadge({ status }: { status: AgentStatus }) {
  const map: Record<AgentStatus, { label: string; variant: 'success' | 'warning' | 'muted' }> = {
    active: { label: 'Activo', variant: 'success' },
    draft: { label: 'Borrador', variant: 'warning' },
    paused: { label: 'Pausado', variant: 'muted' },
  }
  return <Badge variant={map[status].variant}>{map[status].label}</Badge>
}

function InlineList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-lg border border-border bg-background/40 p-4">
      <p className="text-sm font-medium text-foreground mb-2">{title}</p>
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item} className="rounded-md border border-border bg-[hsl(var(--surface))] px-3 py-2 text-xs text-foreground">
            {item}
          </div>
        ))}
      </div>
    </div>
  )
}

function Block({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-background/40 p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm text-foreground mt-2">{value}</p>
    </div>
  )
}